import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const formats = [
  {
    ext: "PNG",
    icon: "ri-image-line",
    color: "bg-emerald-50 text-emerald-600 border-emerald-200",
    title: "PNG Formatı",
    desc: "Şeffaf arka plan desteği ile web ve dijital kullanım için ideal. Yüksek kaliteli piksel tabanlı format.",
    useCases: ["Web sitesi", "Sosyal medya", "E-posta imzası", "Dijital ekranlar"],
    recommended: true,
  },
  {
    ext: "SVG",
    icon: "ri-file-code-line",
    color: "bg-teal-50 text-teal-600 border-teal-200",
    title: "SVG Formatı",
    desc: "Vektör tabanlı format. Sonsuz büyütme imkânı ile baskı ve profesyonel kullanım için mükemmel.",
    useCases: ["Baskı materyalleri", "Büyük format baskı", "Tasarım yazılımları", "Profesyonel kullanım"],
    recommended: false,
  },
  {
    ext: "JPEG",
    icon: "ri-image-2-line",
    color: "bg-cyan-50 text-cyan-600 border-cyan-200",
    title: "JPEG Formatı",
    desc: "Küçük dosya boyutu ile hızlı paylaşım için ideal. Beyaz arka planlı standart görüntü formatı.",
    useCases: ["Hızlı paylaşım", "Mesajlaşma uygulamaları", "Basit baskı", "Arşivleme"],
    recommended: false,
  },
];

const tips = [
  { icon: "ri-printer-line", title: "Baskı İçin", desc: "SVG formatını tercih edin. Sonsuz büyütme ile kalite kaybı yaşamazsınız.", color: "text-emerald-600 bg-emerald-50" },
  { icon: "ri-smartphone-line", title: "Dijital Kullanım İçin", desc: "PNG formatı şeffaf arka plan desteği ile web ve mobil için idealdir.", color: "text-teal-600 bg-teal-50" },
  { icon: "ri-share-line", title: "Hızlı Paylaşım İçin", desc: "JPEG formatı küçük dosya boyutu ile mesajlaşma uygulamaları için uygundur.", color: "text-cyan-600 bg-cyan-50" },
  { icon: "ri-zoom-in-line", title: "Minimum Boyut", desc: "QR kodunuzun en az 2x2 cm boyutunda olmasını sağlayın. Daha küçük boyutlar tarama sorununa yol açabilir.", color: "text-emerald-600 bg-emerald-50" },
];

export default function DownloadPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16">
        <div className="max-w-6xl mx-auto px-6 py-16">
          {/* Header */}
          <div className="text-center mb-14">
            <span className="text-emerald-600 text-sm font-semibold uppercase tracking-widest">İndirme Seçenekleri</span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Doğru Formatı Seçin
            </h1>
            <p className="text-gray-500 max-w-xl mx-auto">
              QR kodunuzu PNG, SVG veya JPEG formatında yüksek çözünürlüklü olarak indirin. Kullanım amacınıza göre en uygun formatı seçin.
            </p>
          </div>

          {/* Format Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {formats.map((fmt) => (
              <div
                key={fmt.ext}
                className={`relative bg-white rounded-2xl border-2 p-7 flex flex-col transition-all hover:scale-[1.01] ${fmt.color}`}
              >
                {fmt.recommended && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                    Önerilen
                  </div>
                )}
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl mb-5 ${fmt.color}`}>
                  <i className={`${fmt.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{fmt.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-5">{fmt.desc}</p>
                <div className="flex-1">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Kullanım Alanları</div>
                  <ul className="space-y-2">
                    {fmt.useCases.map((uc) => (
                      <li key={uc} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-emerald-500 flex-shrink-0"></i>
                        {uc}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to="/generator"
                  className="mt-6 w-full flex items-center justify-center gap-2 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-download-2-line"></i>
                  {fmt.ext} Olarak İndir
                </Link>
              </div>
            ))}
          </div>

          {/* Tips */}
          <div className="bg-white rounded-2xl border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">İndirme İpuçları</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {tips.map((tip) => (
                <div key={tip.title} className="flex flex-col gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${tip.color}`}>
                    <i className={`${tip.icon} text-lg`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm">{tip.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{tip.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-10 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">QR Kodunuzu Oluşturmaya Hazır mısınız?</h2>
            <p className="text-emerald-100 mb-6">Dakikalar içinde profesyonel QR kodunuzu oluşturun ve istediğiniz formatta indirin.</p>
            <Link
              to="/generator"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-emerald-600 font-bold rounded-full hover:bg-emerald-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-qr-code-line"></i>
              Hemen Başla
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
